import { c as _c } from "react/compiler-runtime";
import type { AgentNode } from '@onefootprint/request-types/dashboard';
import { useToast } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { uniqueArtifactKeys } from '../store/field-key';
import type { BundleNode, InputField } from '../store/types';
import { type AgentInput, agentInputFromWire, inputsToWire, nodeToWire } from '../store/wire';
type InputsClipboard = {
  kind: 'inputs';
  inputs: AgentInput[];
};
const useNodeClipboard = () => {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.canvas.copyToast"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    toast
  } = useToast();
  const [, copy] = useCopyToClipboard();
  let t1;
  if ($[1] !== copy || $[2] !== t || $[3] !== toast) {
    t1 = async node => {
      const ok = await copy(JSON.stringify(nodeToWire(node)));
      if (ok) {
        toast({
          title: t(_temp),
          description: t(_temp2)
        });
      }
    };
    $[1] = copy;
    $[2] = t;
    $[3] = toast;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const copyNode = t1;
  let t2;
  if ($[5] !== copy || $[6] !== t || $[7] !== toast) {
    t2 = async inputs => {
      const payload = {
        kind: "inputs",
        inputs: inputsToWire(inputs)
      };
      const ok_0 = await copy(JSON.stringify(payload));
      if (ok_0) {
        toast({
          title: t(_temp3),
          description: t(_temp4)
        });
      }
    };
    $[5] = copy;
    $[6] = t;
    $[7] = toast;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const copyInputs = t2;
  let t3;
  if ($[9] !== copyInputs || $[10] !== copyNode) {
    t3 = {
      copyNode,
      copyInputs,
      readClipboardNode,
      readClipboardInputs
    };
    $[9] = copyInputs;
    $[10] = copyNode;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
const isCopyableNode = (value: unknown): value is AgentNode => {
  if (typeof value !== 'object' || value === null) return false;
  const kind = (value as {
    kind?: unknown;
  }).kind;
  return kind === 'task' || kind === 'rules' || kind === 'managed' || kind === 'decision';
};
const isInputsClipboard = (value: unknown): value is InputsClipboard => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as {
    kind?: unknown;
    inputs?: unknown;
  };
  return v.kind === 'inputs' && Array.isArray(v.inputs);
};

// Paste is a new identity: mint from the current names against the destination graph.
export const withFreshArtifactKeys = (node: AgentNode, taken: Set<string>): AgentNode => {
  if (node.kind !== 'task') return node;
  const used = new Set(taken);
  for (const field of node.fields) used.add(field.key);
  const keys = uniqueArtifactKeys(node.artifacts.map(artifact => artifact.name), used);
  return {
    ...node,
    artifacts: node.artifacts.map((artifact, index) => ({
      ...artifact,
      key: keys[index] ?? artifact.key
    }))
  };
};
const readClipboardNode = async () => {
  try {
    const text = await navigator.clipboard.readText();
    const parsed = JSON.parse(text);
    return isCopyableNode(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
const readClipboardInputs = async (): Promise<InputField[] | null> => {
  try {
    const text = await navigator.clipboard.readText();
    const parsed = JSON.parse(text);
    return isInputsClipboard(parsed) ? parsed.inputs.map(agentInputFromWire) : null;
  } catch {
    return null;
  }
};
export default useNodeClipboard;
function _temp($) {
  return $.title;
}
function _temp2($_0) {
  return $_0.description;
}
function _temp3($_1) {
  return $_1.title;
}
function _temp4($_2) {
  return $_2.description;
}